import * as styles from "./ClosureWeekGrid.css";

import { useAtomValue, useSetAtom } from "jotai";
import type { JSX } from "react";

import { Toggle } from "@frontend/components/atoms/Toggle";
import { useLingui } from "@lingui/react/macro";

import { closureWeeksAtom, toggleWeekAtom } from "../stores/closureWeeks";
import type { ValidatedHoursWeek } from "../types";
import { weekSpan } from "../utils/dates";

export type ClosureWeekGridProps = {
  readOnly?: boolean;
};

export function ClosureWeekGrid({
  readOnly = false,
}: ClosureWeekGridProps): JSX.Element {
  const { t } = useLingui();

  const { weeks, selected } = useAtomValue(closureWeeksAtom);
  const toggle = useSetAtom(toggleWeekAtom);

  return (
    <div className={styles.grid} role="group" aria-label={t`Weeks of the year`}>
      {weeks.map((week) => {
        const blocking = week.validatedHours;

        let reason = null;

        if (null !== blocking) {
          const hourCount = blocking.hourCount;
          const names = blockingNames(blocking);

          reason = t`${hourCount} validated hour(s) this week: ${names}. Closing it stays informative for them and changes none of their hours. The hours may have been worked on another abonnement.`;
        }

        const title = [week.holidayName, reason].filter(Boolean).join("\n");

        return (
          <Toggle
            key={week.number}
            className={
              null === blocking
                ? styles.week
                : `${styles.week} ${styles.validatedWeek}`
            }
            variant="outline"
            pressed={selected.includes(week.number)}
            disabled={readOnly}
            onPressedChange={() => toggle(week.number)}
            title={"" === title ? undefined : title}
          >
            <span className={styles.header}>
              <span className={styles.number}>{t`Week ${week.number}`}</span>

              {null === week.holidayName ? null : (
                <span className={styles.holiday} />
              )}

              {null === blocking ? null : (
                <span className={styles.validatedCount}>
                  {blocking.hourCount}
                </span>
              )}
            </span>

            <span className={styles.dates}>
              {weekSpan(week.start, week.end)}
            </span>
          </Toggle>
        );
      })}
    </div>
  );
}

function blockingNames(blocking: ValidatedHoursWeek): string {
  return blocking.employees
    .map((employee) => `${employee.name} (${employee.hourCount})`)
    .join(", ");
}
